/**
 * The ONE source of pricing numbers. The landing teaser, pricing page,
 * calculator, JSON-LD offers, and FAQ answers all read from here (D-4:
 * band model pending billing confirmation; correction is a one-file change).
 */
export const FREE_LIMIT = 100_000;
export const MILLION = 1_000_000;
export const BAND_PRICE = 10;

/** 100000 → "100,000". The long form, for body copy and meta descriptions. */
export function figure(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * 100000 → "100K", 1000000 → "1M". The short form a Twitter card has room for,
 * derived rather than retyped so the abbreviations track the bands too.
 *
 * Scaled, not divisibility-gated. The old form asked `n % MILLION === 0` and
 * fell through to the `K` branch when it failed, so 1,500,000 abbreviated to
 * "1500K" — not wrong, but not the short form either, and the failure mode is
 * silent. Today's constants never reach it, which is exactly why the shape is
 * pinned by `pricing.test.ts` rather than left to the next band change to
 * discover (#792 review).
 */
export function compactFigure(n: number): string {
  const scaled = (unit: number, suffix: string) =>
    `${Number((n / unit).toFixed(1))}${suffix}`;
  if (n >= MILLION) return scaled(MILLION, "M");
  if (n >= 1_000) return scaled(1_000, "K");
  return figure(n);
}

/**
 * 1000000 → "million". The prose unit the bands are quoted in.
 *
 * The meta descriptions read "per additional million", and the figure scan in
 * `marketing-copy-single-source.test.ts` could not see it: it matched digits
 * and "one million", never the bare word. So `MILLION` was still hard-coded in
 * those strings, in the one file whose whole claim is that it isn't (#792
 * review).
 *
 * A real derivation, not a moved literal: the word is returned only while the
 * constant still means what the word says. Move `MILLION` and the copy falls
 * back to the digits rather than quietly keeping a unit it no longer has.
 */
export function unitWord(n: number): string {
  if (n === MILLION) return "million";
  if (n === 1_000) return "thousand";
  return figure(n);
}

function clamp(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function emailsPerMonth(
  subscribers: number,
  issuesPerMonth: number,
): number {
  return clamp(subscribers) * clamp(issuesPerMonth);
}

/** Flat bands: free to 100K, $10 to 1M, then $10 per started million. */
export function goodsenderMonthly(emails: number): number {
  const n = clamp(emails);
  if (n <= FREE_LIMIT) return 0;
  return BAND_PRICE * Math.ceil(n / MILLION);
}

/** Amazon SES, about $100 per million, pro rated: the infrastructure floor. */
export function sesMonthly(emails: number): number {
  return Math.round((clamp(emails) / MILLION) * 100);
}

/**
 * A typical per subscriber platform, illustrative: free under 2,500
 * subscribers, then about $4 per thousand on the list per month. The copy
 * footnote says figures are illustrative and change often.
 */
export function perSubscriberMonthly(subscribers: number): number {
  const n = clamp(subscribers);
  if (n < 2_500) return 0;
  return Math.round((n / 1_000) * 4);
}

/**
 * The reference scenario for the landing's assembled-stack comparison: a blog
 * with 10,000 subscribers publishing weekly. Chosen because it is the same
 * shape as the first line of `PRICING_PAGE.examples` ("10,000 subscribers,
 * weekly: free"), so the two surfaces cannot quote different answers for the
 * same customer, and because 40,000 emails a month sits inside `FREE_LIMIT`:
 * the comparison's punchline is a real band, not a promotional exception.
 */
export const STACK_SUBSCRIBERS = 10_000;
export const STACK_ISSUES_PER_MONTH = 4;

/**
 * Monthly cost of assembling the same blog out of parts, by category, in US
 * dollars. Ranges rather than points because these are whole product
 * categories: the copy that renders them carries "illustrative and change
 * often" on the same surface (`LANDING.stack.disclaimer`).
 *
 * Categories, never vendors — see the comment on `LANDING.stack`. Keyed by the
 * same `id` as `LANDING.stack.parts`, which holds the names; the numbers live
 * here so the one-source rule that covers every other figure in the copy
 * covers these too. content.test.ts pins that the two lists name the same set.
 *
 * ONLY costs the assembled stack carries and GoodSender does not. The test for
 * a row is therefore not "does a blog need this" but "does GoodSender leave
 * the customer to buy it". Two rows have already failed it (#823 review), and
 * `LANDING.stack.sharedNote` states both exclusions on the page rather than
 * leaving them silent:
 *
 *  - "Domain, SSL, CDN", $2 to $5. A domain is required BOTH ways, since
 *    GoodSender has no subdomains of its own ("A domain or subdomain you
 *    control.", `DomainSetupFields.tsx`; the
 *    `domain` FAQ entry says the same). The certificate was worse than shared:
 *    GoodSender issues it (`blog/customDomainStatus.ts`), so it was never a
 *    customer cost on our side at all.
 *  - "Analytics", $10 to $20. GoodSender ships NO page-view tracking
 *    ("No web-views column: no page-view tracking exists yet", D-603-3 in
 *    `components/analytics/AnalyticsPage.tsx`); a publisher who wants web
 *    numbers supplies their own GA or GTM property (#828's card on
 *    `dashboard/w/$workspaceId/design.tsx`). So web analytics is bought both
 *    ways. What GoodSender's own Analytics page DOES give (opens, clicks,
 *    list growth) is email-side, which the assembled stack gets from the
 *    thing it is already paying for in `esp` — so pricing it again here
 *    double-counted the same capability.
 *
 * Both failures share a shape worth remembering: the row named a category a
 * blog needs rather than a product this replaces.
 */
export const STACK_COSTS: Record<string, { lo: number; hi: number }> = {
  cms: { lo: 25, hi: 200 },
  esp: { lo: 100, hi: 150 },
  forms: { lo: 0, hi: 40 },
};

/** The assembled stack's total range: the sum of every category above. */
export function stackMonthly(): { lo: number; hi: number } {
  return Object.values(STACK_COSTS).reduce(
    (total, part) => ({ lo: total.lo + part.lo, hi: total.hi + part.hi }),
    { lo: 0, hi: 0 },
  );
}

/**
 * "$25 to $200", or "$40" when a category is a single figure. `free` is the
 * word, not "$0": a zero dollar row rendered as a price reads as a rounding
 * artefact, and free is the thing the comparison is actually selling.
 */
export function formatRange(range: { lo: number; hi: number }): string {
  if (range.lo === 0 && range.hi === 0) return "free";
  if (range.lo === range.hi) return `$${range.lo}`;
  return `$${range.lo} to $${range.hi}`;
}

/** Newsletter scenarios used on the pricing page, all with four monthly issues. */
export const EXAMPLE_SUBSCRIBERS = [10_000, 30_000, 100_000, 500_000] as const;
export const EXAMPLE_ISSUES_PER_MONTH = 4;
